import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ICompanyLanguageConfig } from '../common/models/Company'

// Applies a company's language preference to i18next: switches to the configured
// language and, when a custom translation file is set, merges its term overrides
// into the bundle. Shared by the public Employer Branding pages (manifesto/home,
// careers, library) so external visitors see the company's language and custom
// terminology, identically to how App.tsx applies them in-platform. Returns
// whether the current preference is in place, so the app can wait for it.
const useApplyCompanyLanguage = (
  languageConfig?: ICompanyLanguageConfig
): boolean => {
  const { i18n } = useTranslation()

  const preference =
    languageConfig?.language &&
    `${languageConfig.language}|${languageConfig.file ?? ''}`
  const [appliedPreference, setAppliedPreference] = useState<string>()

  useEffect(() => {
    if (!languageConfig?.language || !preference) return

    const apply = async () => {
      await i18n.changeLanguage(languageConfig.language)
      if (!languageConfig.file) {
        await i18n.reloadResources(languageConfig.language, 'translation')
        return
      }
      const data = await fetch(languageConfig.file, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }).then((response) => response.json())
      if (!data) return
      i18n.addResourceBundle(
        languageConfig.language,
        'translation',
        data,
        true,
        true
      )
    }

    // A failed custom file still leaves the language itself in place
    apply()
      .catch(console.error)
      .finally(() => setAppliedPreference(preference))
  }, [preference])

  return !preference || appliedPreference === preference
}

export default useApplyCompanyLanguage
